import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  randomLcg,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3';
import type { ClusterEdge } from './artifact-block-types';

export const CLUSTER_GRAPH_WIDTH = 840;
const PARALLEL_EDGE_SPACING = 28;
// Must accept exactly what FpId::parse_with_prefix does (backend newtypes/src/id/prefix.rs),
// including the `test_` infix sandbox vaults carry.
const FOOTPRINT_ENTITY_ID_PATTERN = /^fp_(?:b)?id_(?:test_)?[A-Za-z0-9]+$/;

export type ClusterEntityKind = 'fp_id' | 'fp_bid' | 'other';

export type ClusterGraphNode = {
  id: string;
  kind: ClusterEntityKind;
};

export type ClusterLayoutNode = ClusterGraphNode &
  SimulationNodeDatum & {
    x: number;
    y: number;
  };

type ClusterSimulationEdge = Omit<ClusterEdge, 'source' | 'target'> &
  SimulationLinkDatum<ClusterLayoutNode> & {
    index: number;
    source: ClusterLayoutNode;
    target: ClusterLayoutNode;
  };

export type ClusterLayoutEdge = ClusterSimulationEdge & {
  path: string;
};

const getClusterEntityKind = (entity: string): ClusterEntityKind => {
  if (!FOOTPRINT_ENTITY_ID_PATTERN.test(entity)) return 'other';
  return entity.startsWith('fp_bid_') ? 'fp_bid' : 'fp_id';
};

export const getClusterEntities = (edges: ClusterEdge[]): ClusterGraphNode[] => {
  const entities = new Map<string, ClusterGraphNode>();

  for (const edge of edges) {
    for (const entity of [edge.source, edge.target]) {
      if (!entities.has(entity)) {
        entities.set(entity, {
          id: entity,
          kind: getClusterEntityKind(entity),
        });
      }
    }
  }

  return [...entities.values()];
};

export const isDenseCluster = (nodeCount: number) => nodeCount > 16;

const getClusterGraphHeight = (nodeCount: number) =>
  isDenseCluster(nodeCount)
    ? Math.min(700, 580 + Math.max(0, nodeCount - 17) * 8)
    : Math.min(560, Math.max(460, 410 + nodeCount * 8));

const getParallelEdgeOffsets = (edges: ClusterSimulationEdge[]) => {
  const groupedEdges = new Map<string, ClusterSimulationEdge[]>();

  for (const edge of edges) {
    const pairKey = [edge.source.id, edge.target.id].sort().join('::');
    const group = groupedEdges.get(pairKey);
    if (group) group.push(edge);
    else groupedEdges.set(pairKey, [edge]);
  }

  const offsets = new Map<number, number>();

  for (const parallelEdges of groupedEdges.values()) {
    parallelEdges.forEach((edge, index) => {
      offsets.set(edge.index, (index - (parallelEdges.length - 1) / 2) * PARALLEL_EDGE_SPACING);
    });
  }

  return offsets;
};

const getClusterEdgePath = (edge: ClusterSimulationEdge, offset: number) => {
  const deltaX = edge.target.x - edge.source.x;
  const deltaY = edge.target.y - edge.source.y;
  const length = Math.max(Math.hypot(deltaX, deltaY), 1);
  const midpointX = (edge.source.x + edge.target.x) / 2;
  const midpointY = (edge.source.y + edge.target.y) / 2;
  const controlX = midpointX + (-deltaY / length) * offset;
  const controlY = midpointY + (deltaX / length) * offset;

  return `M ${edge.source.x} ${edge.source.y} Q ${controlX} ${controlY} ${edge.target.x} ${edge.target.y}`;
};

export const layoutClusterGraph = (
  edges: ClusterEdge[],
  width: number,
): { nodes: ClusterLayoutNode[]; edges: ClusterLayoutEdge[]; height: number } => {
  const graphNodes = getClusterEntities(edges);
  const height = getClusterGraphHeight(graphNodes.length);
  const dense = isDenseCluster(graphNodes.length);
  // The dense forces fill the desktop canvas and would pin nodes to the side walls of a narrower one
  // (a phone), so forceScale shrinks them with the width. Sparse clusters already fit.
  const forceScale = dense ? Math.min(1, width / CLUSTER_GRAPH_WIDTH) : 1;
  const orbitRadius = Math.min(width, height) * (dense ? 0.4 : 0.32);
  const layoutNodes: ClusterLayoutNode[] = graphNodes.map((node, index) => {
    const angle = (index / graphNodes.length) * Math.PI * 2;
    return {
      ...node,
      x: width / 2 + Math.cos(angle) * orbitRadius,
      y: height / 2 + Math.sin(angle) * orbitRadius,
    };
  });
  const byId = new Map(layoutNodes.map(node => [node.id, node]));
  const getLayoutNode = (id: string) => {
    const node = byId.get(id);
    if (!node) throw new Error(`Missing cluster layout node: ${id}`);
    return node;
  };
  const simulationEdges = edges.map<ClusterSimulationEdge>((edge, index) => ({
    attribute: edge.attribute,
    value: edge.value,
    detail: edge.detail,
    evidence: edge.evidence,
    index,
    source: getLayoutNode(edge.source),
    target: getLayoutNode(edge.target),
  }));
  const simulation = forceSimulation(layoutNodes)
    .randomSource(randomLcg(0.42))
    .force(
      'link',
      forceLink<ClusterLayoutNode, ClusterSimulationEdge>(simulationEdges)
        .distance((dense ? 148 : 118) * forceScale)
        .strength(dense ? 0.38 : 0.32),
    )
    .force('charge', forceManyBody().strength((dense ? -500 : -340) * forceScale ** 2))
    .force('center', forceCenter(width / 2, height / 2).strength(dense ? 0.04 : 0.08))
    .force('x', forceX(width / 2).strength(dense ? 0.008 : 0.025))
    .force('y', forceY(height / 2).strength(dense ? 0.012 : 0.025))
    .force('collide', forceCollide((dense ? 46 : 44) * forceScale).iterations(2))
    .stop();

  const ticks = Math.min(300, 170 + layoutNodes.length * 5);
  for (let tick = 0; tick < ticks; tick += 1) simulation.tick();
  simulation.stop();

  const padding = dense ? 52 : 46;
  for (const node of layoutNodes) {
    node.x = Math.max(padding, Math.min(width - padding, node.x));
    node.y = Math.max(padding, Math.min(height - padding, node.y));
  }

  const parallelEdgeOffsets = getParallelEdgeOffsets(simulationEdges);
  const layoutEdges = simulationEdges.map<ClusterLayoutEdge>(edge => ({
    ...edge,
    path: getClusterEdgePath(edge, parallelEdgeOffsets.get(edge.index) ?? 0),
  }));

  return { nodes: layoutNodes, edges: layoutEdges, height };
};
